import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Box,
  Badge,
  Alert,
  Modal,
  Form,
  FormField,
  Input,
  Textarea,
  Select,
  Table
} from '@cloudscape-design/components';
import Grid from '@cloudscape-design/components/grid';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

const PointsSystem = ({ 
  familyMembers = [], 
  currentUser
}) => {
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rules, setRules] = useState([]);
  const [pointsHistory, setPointsHistory] = useState([]);
  const [memberPoints, setMemberPoints] = useState({});

  console.log("currentUser", currentUser)
  // 获取成员角色
  const getMemberRole = (member) => {
    const roleField = member.role || member.role_type || member.user_role;
    if (roleField) {
      return ['Parent', 'parent'].includes(roleField) ? 'parent' : 'child';
    }
    if (['爸', '妈', '爸爸', '妈妈'].includes(member.name)) return 'parent';
    return 'child';
  };

  // 检查是否为家长
  const isParent = () => {
    if (!currentUser) return false;
    return getMemberRole(currentUser) === 'parent';
  };

  // 奖惩记录表单状态
  const [recordForm, setRecordForm] = useState({
    member_id: '',
    action: '',
    points: 0,
    type: 'reward', // reward 或 punishment
    description: ''
  });

  // 加载数据
  useEffect(() => {
    if (currentUser && currentUser.family_id) {
      loadRules();
      loadPointsHistory();
      loadMemberPoints();
    }
  }, [currentUser]);

  // 加载奖惩规则
  const loadRules = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/points-rules/`, {
        params: { family_id: currentUser.family_id }
      });
      setRules(response.data || []);
    } catch (error) {
      console.error('加载奖惩规则失败:', error);
    }
  };

  // 加载奖惩历史
  const loadPointsHistory = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/points-history/`, {
        params: { family_id: currentUser.family_id }
      });
      setPointsHistory(response.data || []);
    } catch (error) {
      console.error('加载奖惩历史失败:', error);
    }
  };

  // 加载成员金币
  const loadMemberPoints = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/families/${currentUser.family_id}/points`);
      setMemberPoints(response.data || {});
    } catch (error) {
      console.error('加载成员金币失败:', error);
    }
  };

  // 处理添加奖惩记录
  const handleAddRecord = async () => {
    if (!recordForm.member_id || !recordForm.action || recordForm.points === 0) {
      setError('请填写完整信息');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // 调用API添加记录
      await axios.post(`${API_BASE_URL}/points-records/`, {
        family_id: currentUser.family_id,
        member_id: recordForm.member_id,
        action: recordForm.action,
        points: recordForm.type === 'punishment' ? -Math.abs(recordForm.points) : Math.abs(recordForm.points),
        type: recordForm.type,
        description: recordForm.description,
        created_by: currentUser.id || currentUser._id
      });

      // 重新加载数据
      await loadPointsHistory();
      await loadMemberPoints();

      setShowRecordModal(false);
      resetRecordForm();
    } catch (error) {
      console.error('添加记录失败:', error);
      setError('添加记录失败：' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 重置表单
  const resetRecordForm = () => {
    setRecordForm({
      member_id: '',
      action: '',
      points: 0,
      type: 'reward',
      description: ''
    });
  };

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // 过滤出孩子成员
  const childMembers = familyMembers.filter(m => getMemberRole(m) === 'child');

  return (
    <SpaceBetween size="l">
      {/* 错误提示 */}
      {error && (
        <Alert type="error" dismissible onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 主内容区域 - 左右分栏 */}
      <Grid gridDefinition={[{ colspan: 5 }, { colspan: 7 }]}>
        {/* 左侧：奖惩明细条目 */}
        <Container
          header={
            <Header variant="h2" description="定义奖励和惩罚的规则">
              奖惩规则
            </Header>
          }
        >
          <SpaceBetween size="m">
            {/* 奖励规则 */}
            <Box>
              <Box variant="h3" padding={{ bottom: 's' }}>🎁 奖励项目</Box>
              <Table
                columnDefinitions={[
                  {
                    id: 'action',
                    header: '行为',
                    cell: item => item.action
                  },
                  {
                    id: 'points',
                    header: '金币',
                    cell: item => (
                      <Box display="flex" alignItems="center">
                        <span style={{ color: '#52c41a', marginRight: '4px' }}>+</span>
                        <span style={{ color: '#52c41a', fontWeight: 'bold' }}>🪙{item.points}</span>
                      </Box>
                    )
                  }
                ]}
                items={rules.filter(r => r.type === 'reward')}
                loadingText="加载中..."
                empty={
                  <Box textAlign="center" color="inherit" padding="s">
                    <b>暂无奖励规则</b>
                    <Box variant="p" color="inherit">
                      请添加奖励项目
                    </Box>
                  </Box>
                }
                variant="embedded"
              />
            </Box>

            {/* 惩罚规则 */}
            <Box>
              <Box variant="h3" padding={{ bottom: 's' }}>⚠️ 惩罚项目</Box>
              <Table
                columnDefinitions={[
                  {
                    id: 'action',
                    header: '行为',
                    cell: item => item.action
                  },
                  {
                    id: 'points',
                    header: '金币',
                    cell: item => (
                      <Box display="flex" alignItems="center">
                        <span style={{ color: '#ff4d4f', marginRight: '4px' }}>-</span>
                        <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>🪙{Math.abs(item.points)}</span>
                      </Box>
                    )
                  }
                ]}
                items={rules.filter(r => r.type === 'punishment')}
                loadingText="加载中..."
                empty={
                  <Box textAlign="center" color="inherit" padding="s">
                    <b>暂无惩罚规则</b>
                    <Box variant="p" color="inherit">
                      请添加惩罚项目
                    </Box>
                  </Box>
                }
                variant="embedded"
              />
            </Box>

            {/* 示例说明 */}
            <Box padding="s" background="color-background-container-content">
              <Box variant="small" color="text-body-secondary">
                <strong>示例：</strong>
                <br />• 帮忙洗碗：+5~10金币
                <br />• 乱发脾气：-5金币
                <br />• 主动完成作业：+10金币
                <br />• 说谎：-10金币
              </Box>
            </Box>
          </SpaceBetween>
        </Container>

        {/* 右侧：奖惩历史 */}
        <Container
          header={
            <Header
              variant="h2"
              description="所有成员的金币获取和扣除记录"
              actions={
                isParent() && (
                  <Button
                    variant="primary"
                    iconName="add-plus"
                    onClick={() => setShowRecordModal(true)}
                  >
                    添加奖惩
                  </Button>
                )
              }
            >
              奖惩历史
            </Header>
          }
        >
          <Table
            columnDefinitions={[
              {
                id: 'member_name',
                header: '成员',
                cell: item => (
                  <Box display="flex" alignItems="center">
                    <Badge color={item.type === 'reward' ? 'green' : 'red'}>
                      {item.member_name}
                    </Badge>
                  </Box>
                )
              },
              {
                id: 'action',
                header: '事项',
                cell: item => item.action
              },
              {
                id: 'points',
                header: '金币变化',
                cell: item => (
                  <Box display="flex" alignItems="center">
                    <span style={{ 
                      color: item.points > 0 ? '#52c41a' : '#ff4d4f',
                      fontWeight: 'bold',
                      marginRight: '4px'
                    }}>
                      {item.points > 0 ? '+' : ''}
                    </span>
                    <span style={{ 
                      color: item.points > 0 ? '#52c41a' : '#ff4d4f',
                      fontWeight: 'bold'
                    }}>
                      🪙{item.points}
                    </span>
                  </Box>
                )
              },
              {
                id: 'description',
                header: '备注',
                cell: item => item.description || '-'
              },
              {
                id: 'created_at',
                header: '时间',
                cell: item => formatDate(item.created_at)
              }
            ]}
            items={pointsHistory}
            loadingText="加载历史记录中..."
            empty={
              <Box textAlign="center" color="inherit" padding="l">
                <b>暂无奖惩记录</b>
                <Box variant="p" color="inherit">
                  还没有任何奖惩记录
                </Box>
              </Box>
            }
            variant="borderless"
            sortingDisabled
          />
        </Container>
      </Grid>

      {/* 添加奖惩记录模态框 */}
      <Modal
        onDismiss={() => {
          setShowRecordModal(false);
          resetRecordForm();
          setError(null);
        }}
        visible={showRecordModal}
        closeAriaLabel="关闭模态框"
        size="medium"
        header="添加奖惩记录"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                variant="link"
                onClick={() => {
                  setShowRecordModal(false);
                  resetRecordForm();
                  setError(null);
                }}
              >
                取消
              </Button>
              <Button
                variant="primary"
                onClick={handleAddRecord}
                loading={loading}
              >
                提交
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <Form>
          <SpaceBetween size="l">
            <FormField label="选择成员（孩子）">
              <Select
                selectedOption={
                  recordForm.member_id 
                    ? { 
                        label: childMembers.find(m => (m.id || m._id) === recordForm.member_id)?.name, 
                        value: recordForm.member_id 
                      }
                    : null
                }
                onChange={({ detail }) => setRecordForm(prev => ({ ...prev, member_id: detail.selectedOption.value }))}
                options={childMembers.map(member => ({
                  label: `${member.name} (🪙${memberPoints[member.id || member._id]?.available || 0})`,
                  value: member.id || member._id
                }))}
                placeholder="选择成员"
              />
            </FormField>

            <FormField label="事项内容">
              <Input
                value={recordForm.action}
                onChange={({ detail }) => setRecordForm(prev => ({ ...prev, action: detail.value }))}
                placeholder="例如：帮忙洗碗、乱发脾气等"
              />
            </FormField>

            <FormField label="类型">
              <Select
                selectedOption={{ label: recordForm.type === 'reward' ? '奖励' : '惩罚', value: recordForm.type }}
                onChange={({ detail }) => setRecordForm(prev => ({ ...prev, type: detail.selectedOption.value }))}
                options={[
                  { label: '🎁 奖励', value: 'reward' },
                  { label: '⚠️ 惩罚', value: 'punishment' }
                ]}
              />
            </FormField>

            <FormField label="金币数量">
              <Input
                type="number"
                value={recordForm.points.toString()}
                onChange={({ detail }) => setRecordForm(prev => ({ ...prev, points: parseInt(detail.value) || 0 }))}
                placeholder="输入金币数量"
              />
              <Box variant="small" color="text-body-secondary" padding={{ top: 'xs' }}>
                {recordForm.type === 'reward' ? '奖励请输入正数，系统会自动添加金币' : '惩罚请输入正数，系统会自动扣除金币'}
              </Box>
            </FormField>

            <FormField label="备注（可选）">
              <Textarea
                value={recordForm.description}
                onChange={({ detail }) => setRecordForm(prev => ({ ...prev, description: detail.value }))}
                placeholder="添加备注信息"
                rows={2}
              />
            </FormField>

            {/* 预览 */}
            {recordForm.member_id && recordForm.points > 0 && (
              <Box padding="s" background="color-background-container-content">
                <SpaceBetween size="xs">
                  <Box fontWeight="bold">预览：</Box>
                  <Box>
                    {childMembers.find(m => (m.id || m._id) === recordForm.member_id)?.name} 
                    {recordForm.type === 'reward' ? ' 将获得 ' : ' 将被扣除 '}
                    <span style={{ 
                      color: recordForm.type === 'reward' ? '#52c41a' : '#ff4d4f',
                      fontWeight: 'bold'
                    }}>
                      {recordForm.type === 'reward' ? '+' : '-'}🪙{recordForm.points}
                    </span>
                  </Box>
                </SpaceBetween>
              </Box>
            )}
          </SpaceBetween>
        </Form>
      </Modal>
    </SpaceBetween>
  );
};

export default PointsSystem;